import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Get } from '../../hooks/useFetch';
import { GetScope } from '../../features/authentication/hooks/useToken.jsx';
import { getSponsor } from './sponsorKit';

// Shared Wizlo intake-form plumbing. Both the product portal (storefront.jsx)
// and the HRT portal (md2Hrt.jsx) embed the same form and ran byte-identical
// copies of this handshake; they now share one implementation so a change to
// the contract only has to be made once.
//
// The handshake, per Wizlo's embed docs: the form posts `wizlo-form-ready` or
// `wizlo-form-visible` when it can accept data, and `wizlo-form-resize` with
// its content height. We reply exactly once with `wizlo-form-init-patient`.
// Only messages from the form's own origin are honoured, and the patient
// payload uses exactly the documented keys — no aliases, no early blind sends.

// buildPatient maps one of our customers onto Wizlo's documented patient keys.
export const buildPatient = (customer) => {
  if (!customer) return null;
  const patient = {
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    email: customer.emailAddress ?? '',
  };
  const phone = (customer.phoneNumbers?.[0]?.number ?? '').replace(/\D/g, '');
  if (phone) patient.phone = phone;
  if (customer.birthDate) patient.dateOfBirth = String(customer.birthDate).slice(0, 10);
  return patient;
};

// useWizloCustomer loads the logged-in customer for autofill. customerLoaded
// gates the iframe so we never mount it before we know whether we have data.
export const useWizloCustomer = () => {
  const scope = GetScope();
  const [customer, setCustomer] = useState(null);
  const [customerLoaded, setCustomerLoaded] = useState(!scope);
  useEffect(() => {
    if (!scope) return;
    Get(`/api/v1/Customers/${encodeURIComponent(scope)}`,
      (c) => { setCustomer(c); setCustomerLoaded(true); },
      () => setCustomerLoaded(true));
  }, [scope]);
  return { customer, customerLoaded };
};

// useWizloCoupon resolves the coupon to show above the form.
//
// ONLY the sponsor's coupon for THIS store, and only when they actually made
// one. A coupon is something you create to share with other people — it is
// never a discount for yourself — so the viewer's own coupon must never appear
// here. The sponsor record is already per-slug, and resolve-sponsor scopes
// couponCode to this marketplace (customerMarketplaceCoupon), so a sponsor
// with a coupon on a sibling store contributes nothing: getSponsor blanks
// couponCode when a referral is adopted across stores.
export const useWizloCoupon = (slug) => {
  const read = () => {
    const sp = getSponsor(slug);
    if (!sp?.couponCode) return null;
    return { code: sp.couponCode, percent: sp.discountPercent || null };
  };
  const [coupon, setCoupon] = useState(read);

  // The sponsor is often resolved after mount (the referral link is read, then
  // resolve-sponsor fills in their coupon for this store). Re-read on the same
  // change event the sponsor banner listens to, or a real coupon would only
  // appear after a reload.
  useEffect(() => {
    const sync = () => setCoupon(read());
    window.addEventListener('mktsponsor-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('mktsponsor-change', sync);
      window.removeEventListener('storage', sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return coupon;
};

// useWizloFrame wires the message handshake and returns the iframe ref plus the
// height the form asked for.
export const useWizloFrame = ({ url, customer, autofillEnabled, couponCode }) => {
  const iframeRef = useRef(null);
  const postedRef = useRef(false);
  const [frameHeight, setFrameHeight] = useState(null);

  useEffect(() => {
    if (!url) return undefined;
    let wizloOrigin = null;
    try { wizloOrigin = new URL(url).origin; } catch { return undefined; }

    const patient = buildPatient(customer);
    // Best effort only: Wizlo documents no coupon prefill, so we attach the
    // code and also send a coupon-shaped init message. Both are scoped to the
    // form's origin and are ignored harmlessly if unsupported — the visible
    // banner above the form is what the patient actually relies on.
    if (patient && couponCode) patient.couponCode = couponCode;

    const onMessage = (event) => {
      if (event.origin !== wizloOrigin) return;
      const data = event.data || {};

      if ((data.type === 'wizlo-form-ready' || data.type === 'wizlo-form-visible')
        && autofillEnabled && patient && !postedRef.current) {
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        postedRef.current = true;
        win.postMessage({ type: 'wizlo-form-init-patient', patient }, wizloOrigin);
        if (couponCode) {
          win.postMessage({ type: 'wizlo-form-init-coupon', couponCode }, wizloOrigin);
        }
      }

      if (data.type === 'wizlo-form-resize' && typeof data.height === 'number') {
        setFrameHeight(Math.max(data.height, 600));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [url, customer, autofillEnabled, couponCode]);

  return { iframeRef, frameHeight };
};

// WizloCouponBanner shows the code the patient should enter at Wizlo's
// checkout, with a copy button.
export const WizloCouponBanner = ({ coupon, accent }) => {
  const [copied, setCopied] = useState(false);
  if (!coupon?.code) return null;

  const copy = () => {
    navigator.clipboard?.writeText(coupon.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }, () => { /* clipboard blocked — the code is on screen anyway */ });
  };

  return <div style={{
    border: `1px solid ${accent ?? '#3450a8'}`, borderRadius: 12, padding: '12px 16px',
    marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  }}>
    <span style={{ fontSize: 14 }}>
      {coupon.percent ? <><strong>{coupon.percent}% off</strong> with code </> : 'Use code '}
      <strong style={{ letterSpacing: 0.5 }}>{coupon.code}</strong>
      <span style={{ opacity: 0.75 }}> — enter it at checkout below.</span>
    </span>
    <button type="button" onClick={copy} style={{
      marginLeft: 'auto', border: `1px solid ${accent ?? '#3450a8'}`, background: 'transparent',
      color: 'inherit', borderRadius: 999, padding: '6px 16px', fontSize: 13, cursor: 'pointer',
    }}>{copied ? 'Copied' : 'Copy code'}</button>
  </div>;
};

WizloCouponBanner.propTypes = {
  coupon: PropTypes.object,
  accent: PropTypes.string,
};
